import React, { Fragment } from "react";
import moment from "moment";
import clsx from "clsx";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
} from "recharts";

import {
  Metric,
  Trend,
  selectFormat,
  StandardTooltip,
  getIcon,
  SOCIALS_COLORS,
} from "./../Utils/Utils.js";

import Heading from "../Elements/Heading.js";

const sectionColors = ["#343a40", "#5a6169", "#868e96", "#adb5bd"];

const PrimeSeriesSocialHighlights = (props) => {
  const data = props.data.phases;

  const renderCharts = (data, type) => {
    return data.map((chart, index) => {
      return (
        <Fragment key={index}>
          <div className="d-flex flex-row align-items-center">
            <span className="trend__label text-capitalize mr-1 ml-0">
              {chart.name}
            </span>

            <Metric size="xs" type={type} {...chart.total} />
          </div>
          {chart.data && (
            <ResponsiveContainer height={35}>
              <BarChart
                data={chart.data}
                layout="vertical"
                width={100}
                height={15}
                margin={{ top: 0, right: 0, left: 0, bottom: 10 }}
                barCategoryGap={0}
              >
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = chart.data;
                        if (payload !== null) {
                          // beucause the payload always return 1 element.. we get it
                          const tooltipData = payload[0];
                          // we create the tooltips based on the keys of the object `tooltipData`, however, we ignore the "total" attribute
                          // get the attributes/values in an array of key/value pairs
                          let tooltips = Object.entries(tooltipData)
                            // defines which attribute should be removed from the list
                            .filter((t) => t[0] !== "total")
                            // map the key/value pair as tooltip (eg. [ 'organic', 41682376 ])
                            .map((t, idx) => (
                              <div className="standard-tooltip__item" key={idx}>
                                <span className="standard-tooltip__key">
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{
                                      // get the background color according the index of the field
                                      background:
                                        SOCIALS_COLORS[chart.name][idx],
                                    }}
                                  />
                                  {/* key */}
                                  <span
                                    className={clsx(
                                      "standard-tooltip__key",
                                      "text-capitalize"
                                    )}
                                  >
                                    {t[0]}
                                  </span>
                                </span>
                                <span className="standard-tooltip__value">
                                  {/* value */}
                                  {selectFormat(type, t[1])}
                                </span>
                              </div>
                            ));

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header text-capitalize">
                                  {chart.name}
                                </span>
                              </header>
                              <main className="standard-tooltip__main">
                                {tooltips}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
                <XAxis type="number" hide />
                <YAxis type="category" hide />
                {/* {renderBars} */}
                <Bar
                  stackId="stacked"
                  dataKey={
                    chart.data[0].organic
                      ? "organic"
                      : chart.data[0].feed
                      ? "feed"
                      : "value"
                  }
                  fill={SOCIALS_COLORS[chart.name][0]}
                />
                {chart.data[0].organic ||
                  (chart.data[0].stories && (
                    <Bar
                      stackId="stacked"
                      dataKey={
                        chart.data[0].organic
                          ? "paid"
                          : chart.data[0].stories
                          ? "stories"
                          : "total"
                      }
                      fill={
                        chart.data[0].organic || chart.data[0].stories
                          ? SOCIALS_COLORS[chart.name][1]
                          : "#fff"
                      }
                    />
                  ))}

                <Bar stackId="stacked" dataKey={"total"} fill={"#fff"} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Fragment>
      );
    });
  };

  const renderHeader = (data) => (
    <>
      <Heading size={5} className={"mb-2"}>
        {data.label}
      </Heading>
      <div className="d-flex mb-2">
        <div className="mr-2">
          <Metric size="lg" type={data.type} value={data.total.value} />
        </div>
        {data.total.change ? (
          <div className="d-flex flex-column mt-1 ml-1">
            <Trend {...data.total} />
          </div>
        ) : null}
      </div>
    </>
  );

  const renderRates = (data, type) => (
    <div className="prime-series-social__rates">
      {data &&
        data.map((d, i) => (
          <div key={i} className="mb-2">
            <div className="d-flex flex-row align-items-center">
              <div className="mt-n1">{getIcon(d.name, "1.5rem")}</div>
              <div className="ml-1">
                <Metric size="md" type={type} {...d.total} />
              </div>
            </div>

            {d.compToAvg ? (
              <div className="comp-to-avg">
                <Trend
                  size="xs"
                  type={type}
                  {...d.compToAvg}
                  label="Comp to Avg"
                />
              </div>
            ) : null}
          </div>
        ))}
    </div>
  );

  const MetricRow = ({ label, metric, type }) => {
    if (!metric) return null;

    return (
      <div className="row d-flex flex-row align-items-baseline">
        <div className="col-3 d-flex justify-content-end">
          <Metric size="xs" type={type} {...metric} />
        </div>
        <div className="col-8">
          <span className="text-muted">{label}</span>
        </div>
      </div>
    );
  };

  const renderHashtags = (hashtags) => (
    <div className="d-flex flex-column">
      {hashtags.map((hashtagData, i) => (
        <Fragment key={hashtagData.id || i}>
          {" "}
          <Heading size={5} className={"mb-2 mt-3 pl-3 font-weight-bold"}>
            {hashtagData.hashtag}
          </Heading>
          <MetricRow label="Views" metric={hashtagData.views} type="number" />
          <MetricRow
            label="Actions"
            metric={hashtagData.actions}
            type="number"
          />
          <MetricRow
            label="Impressions"
            metric={hashtagData.impressions}
            type="number"
          />
          <MetricRow
            label="Video View Rate"
            metric={hashtagData.view_rate}
            type="percentage"
          />
          <MetricRow
            label="Engagements"
            metric={hashtagData.engagements}
            type="number"
          />
          <MetricRow
            label="Engagement Rate"
            metric={hashtagData.engagement_rate}
            type="percentage"
          />
        </Fragment>
      ))}
    </div>
  );

  const renderSection = (widgets) => (
    <div className="row prime-series-social__separator">
      {widgets.map((widget, i) => {
        const hashtags = widget.cells.find((d) => d.label === "Hashtags");
        const rates = widget.cells.slice(
          2,
          widget.cells.length - (hashtags ? 1 : 0)
        );

        // inject the max value inside each chart of the widget as reference for the max portion in size
        widget.cells[0].charts.forEach((c) =>
          c.data.forEach((d) => (d.total = widget.cells[0].total.value))
        );
        return (
          <div className="col-6 col-lg-3" key={i}>
            <div
              className="prime-series-social__header mb-4 pl-3 pt-1 pb-1"
              style={{
                backgroundColor: sectionColors[i],
                color: i > 1 ? "#000" : "#fff",
              }}
            >
              <Heading size={5} className={"m-0"}>
                {widget.name}{" "}
                <div className="prime-series-social__header--dates mt-1">
                  {moment(widget.start).format("MMM DD, YYYY")} -{" "}
                  {moment(widget.end).format("MMM DD, YYYY")}
                </div>
              </Heading>
            </div>
            <div className="prime-series-social__widget">
              {renderHeader(widget.cells[0])}
              {renderCharts(widget.cells[0].charts, widget.cells[0].type)}
              <hr className="social-performance__hr--dotted" />
              {renderHeader(widget.cells[1])}
              {renderCharts(widget.cells[1].charts, widget.cells[1].type)}
              <hr className="social-performance__hr--dotted" />
              {rates &&
                rates.map((rate, i) => (
                  <Fragment key={i}>
                    <Heading size={5} className={"mb-2"}>
                      {rate.label}
                    </Heading>
                    {renderRates(rate.charts, rate.type)}
                    <hr className="social-performance__hr--dotted" />
                  </Fragment>
                ))}
              {hashtags && (
                <>
                  <Heading size={5} className={"mb-2"}>
                    {hashtags.label}
                  </Heading>
                  {renderHashtags(hashtags.rows)}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
  const renderSections = () => <section>{renderSection(data)}</section>;

  return renderSections();
};
export default PrimeSeriesSocialHighlights;
